<template>
  <Teleport to="body">
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-content">
        <button class="close-btn" @click="$emit('close')">&times;</button>

        <!-- Main Tabs for Login/Register -->
        <div class="tabs" v-if="!isForgotPassword && !isSetup">
          <button :class="{ active: isLogin }" @click="isLogin = true">{{ $t('login') }}</button>
          <button :class="{ active: !isLogin }" @click="isLogin = false">{{ $t('register') }}</button>
        </div>

        <div class="tabs" v-if="isSetup">
           <button class="active">{{ $t('setupAccount') || 'Setup Account' }}</button>
        </div>

        <div class="tabs" v-if="isForgotPassword">
           <button class="active">{{ $t('forgotPassword') || 'Forgot Password' }}</button>
        </div>

        <!-- Login Form -->
        <form v-if="isLogin && !isForgotPassword && !isSetup" @submit.prevent="handleLogin" class="auth-form">
          <div class="form-group">
            <input type="tel" v-model="loginForm.phone" :placeholder="$t('phone')" required />
          </div>
          <div class="form-group">
            <input type="password" v-model="loginForm.password" :placeholder="$t('password')" required />
          </div>
          <div class="forgot-pwd-link">
             <a href="#" @click.prevent="isForgotPassword = true">{{ $t('forgotPasswordLink') || 'Forgot Password?' }}</a>
          </div>
          <button type="submit" class="submit-btn" :disabled="loading">
            {{ loading ? $t('loading') : $t('login') }}
          </button>
          <p v-if="error" class="error-msg">{{ error }}</p>
        </form>

        <!-- Register Form -->
        <form v-if="!isLogin && !isForgotPassword && !isSetup" @submit.prevent="handleRegister" class="auth-form">
          <div class="form-group">
            <input type="text" v-model="registerForm.name" :placeholder="$t('name')" required />
          </div>
          <div class="form-group">
            <input type="tel" v-model="registerForm.phone" :placeholder="$t('phone')" required />
          </div>
          <div class="form-group">
            <input type="email" v-model="registerForm.email" :placeholder="$t('email') || 'Email'" required />
          </div>
          <div class="form-group">
            <input type="password" v-model="registerForm.password" :placeholder="$t('password')" required />
          </div>
          <button type="submit" class="submit-btn" :disabled="loading">
            {{ loading ? $t('loading') : $t('register') }}
          </button>
          <p v-if="error" class="error-msg">{{ error }}</p>
        </form>

        <!-- Setup Account Form (For existing phone numbers without password) -->
        <form v-if="isSetup" @submit.prevent="handleSetup" class="auth-form">
           <p class="setup-desc">{{ $t('setupAccountDesc') || 'You already have an account linked to this phone. Please add an email and password to secure it.' }}</p>
           <div class="form-group">
            <input type="email" v-model="setupForm.email" :placeholder="$t('email') || 'Email'" required />
          </div>
          <div class="form-group">
            <input type="password" v-model="setupForm.password" :placeholder="$t('password')" required />
          </div>
          <button type="submit" class="submit-btn" :disabled="loading">
            {{ loading ? $t('loading') : ($t('save') || 'Save') }}
          </button>
          <p v-if="error" class="error-msg">{{ error }}</p>
        </form>

        <!-- Forgot Password Form -->
        <form v-if="isForgotPassword" @submit.prevent="handleForgotPassword" class="auth-form">
           <p class="setup-desc">{{ $t('forgotPasswordDesc') || 'Enter your email to receive a password reset link.' }}</p>
           <div class="form-group">
            <input type="email" v-model="forgotForm.email" :placeholder="$t('email') || 'Email'" required />
          </div>
          <div class="forgot-pwd-link" style="text-align:center;">
             <a href="#" @click.prevent="isForgotPassword = false">{{ $t('backToLogin') || 'Back to Login' }}</a>
          </div>
          <button type="submit" class="submit-btn" :disabled="loading">
            {{ loading ? $t('loading') : ($t('sendLink') || 'Send Link') }}
          </button>
          <p v-if="error" class="error-msg">{{ error }}</p>
          <p v-if="successMsg" class="success-msg">{{ successMsg }}</p>
        </form>

      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { fetchApi } from '../../utils/api'
import { API_BASE_URL } from '../../config'
import store from '../../store'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const emit = defineEmits(['close'])
const isLogin = ref(true)
const isSetup = ref(false)
const isForgotPassword = ref(false)

const loading = ref(false)
const error = ref('')
const successMsg = ref('')

const loginForm = reactive({ phone: '', password: '' })
const registerForm = reactive({ name: '', phone: '', email: '', password: '' })
const setupForm = reactive({ phone: '', email: '', password: '' })
const forgotForm = reactive({ email: '' })

async function handleLogin() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetchApi(`${API_BASE_URL}?token=test&action=customerLogin`, {
      method: 'POST',
      body: JSON.stringify({
        action: 'login',
        phone: loginForm.phone,
        password: loginForm.password
      })
    })
    const data = await res.json()
    if (data.success) {
      store.login(data.token, data.user)
      emit('close')
    } else {
      if(data.action === 'needs_setup') {
         setupForm.phone = loginForm.phone
         isSetup.value = true
      } else {
         error.value = data.message || 'Login failed'
      }
    }
  } catch (e) {
    error.value = 'Network error'
  } finally {
    loading.value = false
  }
}

async function handleRegister() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetchApi(`${API_BASE_URL}?token=test&action=customerLogin`, {
      method: 'POST',
      body: JSON.stringify({
        action: 'register',
        name: registerForm.name,
        phone: registerForm.phone,
        email: registerForm.email,
        password: registerForm.password
      })
    })
    const data = await res.json()
    if (data.success) {
      store.login(data.token, data.user)
      emit('close')
    } else {
      if(data.action === 'needs_setup') {
         setupForm.phone = registerForm.phone
         isSetup.value = true
      } else {
         error.value = data.message || 'Registration failed'
      }
    }
  } catch (e) {
    error.value = 'Network error'
  } finally {
    loading.value = false
  }
}

async function handleSetup() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetchApi(`${API_BASE_URL}?token=test&action=customerLogin`, {
      method: 'POST',
      body: JSON.stringify({
        action: 'setup',
        phone: setupForm.phone,
        email: setupForm.email,
        password: setupForm.password
      })
    })
    const data = await res.json()
    if (data.success) {
      store.login(data.token, data.user)
      emit('close')
    } else {
      error.value = data.message || 'Setup failed'
    }
  } catch (e) {
    error.value = 'Network error'
  } finally {
    loading.value = false
  }
}

async function handleForgotPassword() {
  loading.value = true
  error.value = ''
  successMsg.value = ''
  try {
    const res = await fetchApi(`${API_BASE_URL}?token=test&action=customerForgotPassword`, {
      method: 'POST',
      body: JSON.stringify({
        email: forgotForm.email
      })
    })
    const data = await res.json()
    if (data.success) {
      successMsg.value = t('resetLinkSent') || 'Reset link sent successfully to your email.'
      forgotForm.email = ''
    } else {
      error.value = data.message || 'Failed to send reset link'
    }
  } catch (e) {
    error.value = 'Network error'
  } finally {
    loading.value = false
  }
}

</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #64748b;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #f1f5f9;
}

.tabs {
  display: flex;
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.tabs button {
  flex: 1;
  padding: 12px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  color: #94a3b8;
  border-bottom: 2px solid transparent;
  transition: all 0.3s;
  font-size: 1rem;
}

.tabs button.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
}

.form-group {
  margin-bottom: 16px;
}

.form-group input {
  width: 100%;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus {
  border-color: #3b82f6;
}

.submit-btn {
  width: 100%;
  padding: 14px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.2s;
}

.submit-btn:hover {
  background: #2563eb;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-msg {
  color: #ef4444;
  margin-top: 12px;
  font-size: 0.9rem;
  text-align: center;
}

.success-msg {
  color: #10b981;
  margin-top: 12px;
  font-size: 0.9rem;
  text-align: center;
}

.forgot-pwd-link {
   margin-bottom: 15px;
   text-align: right;
}

.forgot-pwd-link a {
   color: #3b82f6;
   font-size: 0.9rem;
   text-decoration: none;
}
.forgot-pwd-link a:hover {
   text-decoration: underline;
}

.setup-desc {
   font-size: 0.95rem;
   color: #64748b;
   margin-bottom: 15px;
   text-align: center;
}
</style>
